import { useMemo } from 'react';
import { Box, Column, Row, SmartTooltip, Text } from '~/commonComponents';
import { CandidateStatus, CandidateStatusMapper } from '~/domain/PermanentJobs';
import { Colors } from '~/themes/colors';

const TOOLTIP_MAP: Record<CandidateStatus, string> = {
  [CandidateStatus.Applied]:
    'The candidate has active, pending applications for onsite interviews for this job.',
  [CandidateStatus.AppExpired]: 'The candidate applied but all applications have expired.',
  [CandidateStatus.Cancelled]: 'The candidate has cancelled their application.',
  [CandidateStatus.Hired]: 'The candidate has been marked as hired.',
  [CandidateStatus.Interviewed]: 'The candidate has completed at least one onsite interview.',
  [CandidateStatus.Rejected]: 'The candidate has been rejected.',
  [CandidateStatus.Scheduled]: 'The candidate is scheduled for one or more onsite interviews.',
};

const SEGMENT_FILL_COUNT: Record<CandidateStatus, number> = {
  [CandidateStatus.Applied]: 1,
  [CandidateStatus.AppExpired]: 1,
  [CandidateStatus.Cancelled]: 1,
  [CandidateStatus.Hired]: 3,
  [CandidateStatus.Interviewed]: 3,
  [CandidateStatus.Rejected]: 3,
  [CandidateStatus.Scheduled]: 2,
};

const SEGMENT_COLOR: Record<CandidateStatus, string> = {
  [CandidateStatus.Applied]: Colors.secondary_500,
  [CandidateStatus.AppExpired]: Colors.warning_500,
  [CandidateStatus.Cancelled]: Colors.warning_500,
  [CandidateStatus.Hired]: Colors.success_500,
  [CandidateStatus.Interviewed]: Colors.secondary_500,
  [CandidateStatus.Rejected]: Colors.neutral_400,
  [CandidateStatus.Scheduled]: Colors.secondary_300,
};

const TOTAL_SEGMENTS = 3;

interface IProcessStepCellProps {
  status: CandidateStatus;
}

export const ProcessStepCell = ({ status }: IProcessStepCellProps) => {
  const fillCount = SEGMENT_FILL_COUNT[status];
  const segmentColor = SEGMENT_COLOR[status];
  const tooltipText = TOOLTIP_MAP[status];

  const fmtLabel = useMemo(() => {
    const label = CandidateStatusMapper.toProcessStep(status);

    // TODO: Update directly on `CandidateStatusMapper.toProcessStep` once the FF is 100% rolled out
    if (status === CandidateStatus.Applied) {
      return 'Applied';
    }

    // TODO: Update directly on `CandidateStatusMapper.toProcessStep` once the FF is 100% rolled out
    if (status === CandidateStatus.Cancelled) {
      return CandidateStatusMapper.toProcessStep(CandidateStatus.AppExpired);
    }

    return label;
  }, [status]);

  return (
    <SmartTooltip
      content={
        <Text bold color={Colors.white}>
          {tooltipText}
        </Text>
      }
      delay={250}
      position="bottom"
    >
      <Column gap={4} marginLeft={12}>
        <Row gap={4}>
          {Array.from({ length: TOTAL_SEGMENTS }).map((_, index) => (
            <Box
              key={index}
              borderRadius={8}
              height={8}
              width={20}
              backgroundColor={index < fillCount ? segmentColor : Colors.neutral_250}
            />
          ))}
        </Row>
        <Text color={Colors.neutral_500}>{fmtLabel}</Text>
      </Column>
    </SmartTooltip>
  );
};
